import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import "./clients.css";
import AddClientDialog from "./add-client/add-client";
import { useNavigate } from "react-router-dom";
import Notification from "../../../components/notification/notification";
import Table from "../../../components/table/table";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import TableHeaderTextField from "../../../components/table-header-text-filed/table-header-text-filed";
import { useDispatch, useSelector } from "react-redux";
import { getAllClientsRequest } from "../../../redux/clients/slice";
import ContactSpectrum from "../../../components/contact-spectrum/constact-spectrum";

const NameRenderer = ({ value, id, rowData }: any) => {
  const navigate = useNavigate();
  const selectedRow = rowData?.filter((data: any) => data.id === id)[0];
  const onViewDetailsClick = () => {
    navigate(`/clients/${id}`, {
      state: {
        ...selectedRow,
        breadcrumb: {
          names: {
            [id]: value,
          },
        },
      },
    });
  };
  return (
    <Box>
      <Button onClick={onViewDetailsClick} sx={{ textTransform: "none" }}>
        <Typography>{value}</Typography>
      </Button>
    </Box>
  );
};

export default function Clients() {
  const dispatch = useDispatch();

  const [openDialog, setOpenDialog] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [addedClient, setAddedClient] = useState<any>();
  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [filterParams, setFilterParams] = useState({});

  const { loading } = useSelector((state: any) => state.clients);
  const { allClients } = useSelector((state: any) => state.clients);

  const rowCountRef = useRef(allClients?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (allClients?.totalCount !== undefined) {
      rowCountRef.current = allClients?.totalCount;
    }
    return rowCountRef.current;
  }, [allClients?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "clientName",
      headerName: "Client Name",
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <NameRenderer value={params?.value} id={params?.id} rowData={rowData} />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="clientName"
          sortField="client_name"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "primaryRelationshipManager",
      headerName: "Relationship Manager (Primary)",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="relationshipManager"
          sortField="relationship_manager"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
  }, []);

  useEffect(() => {
    setRowData(allClients?.items);
  }, [allClients]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      dispatch(getAllClientsRequest(filters));
    }
  }, [filterParams]); // eslint-disable-line

  const handleClick = () => {
    setOpenDialog(true);
  };

  const addUser = (client: any) => {
    setAddedClient(client);
    setShowAlert(true);
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
  };

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  return (
    <>
      <ContactSpectrum />
      <Breadcrumb />
      <Typography sx={{ ml: 2 }} className="title">
        Clients
      </Typography>
      <Box
        sx={{ mt: 3 }}
        className="clients-header"
        justifyContent={"flex-end"}
      >
        <Button
          id="add-button"
          variant="contained"
          disableElevation
          onClick={handleClick}
          startIcon={<AddIcon />}
        >
          Add Client
        </Button>
      </Box>

      <Box
        className="clients-header"
        sx={{ my: 1.5, justifyContent: "flex-end" }}
      >
        <IconButton
          aria-label="filter"
          onClick={() => onFilterClick(!showTableFilter)}
        >
          {showTableFilter ? (
            <FilterAltOffOutlinedIcon color="primary" />
          ) : (
            <FilterAltOutlinedIcon color="primary" />
          )}
        </IconButton>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          rowData={rowData}
          rowCount={rowCount}
          loading={loading}
          columnDef={column}
          headerHeight={headerHeight}
          disableColumnMenu={true}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          checkboxSelection={false}
        />
      </Box>

      <AddClientDialog
        showDialog={openDialog}
        closeDialog={() => setOpenDialog(false)}
        addUser={addUser}
      />

      <Notification
        show={showAlert}
        title="Client Added Successfully"
        message={`${addedClient?.clientName} has been created.`}
        onClose={() => setShowAlert(false)}
      />
    </>
  );
}
